import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Space,
  message,
  Row,
  Col,
  Table,
  Popconfirm,
  Modal,
  Tooltip,
  Select,
  Tag,
  Dropdown,
  Upload,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  SearchOutlined,
  ClearOutlined,
  ExportOutlined,
  ImportOutlined,
} from '@ant-design/icons';
// 旧的 options 方式已迁移到独立数据表
import statusTranslationService, { StatusTranslationItem as STItem } from '../services/statusTranslation';
import { MONITORED_NATION_OPTIONS } from '../utils/trademarkUrls';
import useDocumentTitle from '../hooks/useDocumentTitle';
import userPreferenceService from '../services/userPreference';
import usePermissions from '../hooks/usePermissions';
import ListHelpButton from '../components/ListHelpButton';

interface StatusTranslationItem { english: string; chinese: string; id?: number; nation?: string }

const STATUS_TRANSLATION_TEXT_MAX_LENGTH = 500;

const StatusTranslation: React.FC = () => {
  useDocumentTitle('状态翻译 - 商标监控');
  const permissions = usePermissions();
  const [form] = Form.useForm();
  const [translations, setTranslations] = useState<StatusTranslationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [nation, setNation] = useState<string | undefined>(undefined); // 列表筛选用
  const [formNation, setFormNation] = useState<string | undefined>(undefined); // 表单用
  const [pagination, setPagination] = useState<{ current: number; pageSize: number }>({ current: 1, pageSize: 20 });
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const canImport = permissions.hasActionAccess('status-translation', 'import');

  useEffect(() => {
    loadTranslations();
  }, [nation]);

  // 初始化加载每页条数偏好
  useEffect(() => {
    const loadPageSize = async () => {
      try {
        const saved = await userPreferenceService.getPageSizeSettings('statusTranslations');
        if (saved && saved > 0 && saved !== pagination.pageSize) {
          setPagination(prev => ({ ...prev, pageSize: saved, current: 1 }));
        }
      } catch {}
    };
    loadPageSize();
  }, []);

  const loadTranslations = async () => {
    try {
      setLoading(true);
      const res = await statusTranslationService.list({ nation, per_page: 1000 });
      if (res.success) {
        setTranslations(res.data.data || []);
      } else {
        setTranslations([]);
      }
    } catch (error) {
      console.error('Failed to load translations:', error);
      setTranslations([]);
    } finally {
      setLoading(false);
    }
  };

  const upsert = async (item: StatusTranslationItem, id?: number) => {
    try {
      setLoading(true);
      const payload: STItem = { english: item.english, chinese: item.chinese, nation: formNation };
      const res = id
        ? await statusTranslationService.update(id, payload)
        : await statusTranslationService.create(payload);
      if (res.success) {
        message.success(id ? '更新成功' : '添加成功');
        await loadTranslations();
      } else {
        message.error(res.message || '保存失败');
      }
    } catch (e: any) {
      message.error(e?.response?.data?.message || '保存失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: StatusTranslationItem) => {
    const english = values.english.trim();
    const chinese = values.chinese.trim();

    if (!english || !chinese) {
      message.warning('请输入英文状态和中文翻译');
      return;
    }

    if (editingIndex !== null) {
      // editingIndex 现在存储的是记录的 id
      await upsert({ english, chinese }, editingIndex);
      setEditingIndex(null);
    } else {
      // 添加模式：同国家下英文唯一（前端快速校验）
      if (translations.some(t => t.english.toLowerCase() === english.toLowerCase())) {
        message.warning('该英文状态已存在');
        return;
      }
      await upsert({ english, chinese });
    }
    form.resetFields();
  };

  const handleEdit = (record: StatusTranslationItem) => {
    form.setFieldsValue(record);
    if (record.nation) setFormNation(record.nation);
    // 使用 id 保存当前编辑的记录
    setEditingIndex(record.id || null);
  };

  const handleDelete = async (record: StatusTranslationItem) => {
    if (!record.id) return;
    try {
      setLoading(true);
      const res = await statusTranslationService.delete(record.id);
      if (res.success) {
        message.success('删除成功');
        await loadTranslations();
      } else {
        message.error(res.message || '删除失败');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setEditingIndex(null);
    setModalVisible(false);
  };

  const handleAdd = () => {
    setEditingIndex(null);
    form.resetFields();
    setFormNation(undefined); // 清空表单国家选择
    setModalVisible(true);
  };

  const handleBatchDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要删除的翻译');
      return;
    }

    Modal.confirm({
      title: `确定删除选中的 ${selectedRowKeys.length} 个翻译吗？`,
      content: '删除后无法恢复',
      onOk: async () => {
        for (const key of selectedRowKeys) {
          const idx = Number(key);
          const row = translations[idx] as STItem;
          if (row?.id) {
            await statusTranslationService.delete(row.id);
          }
        }
        setSelectedRowKeys([]);
        await loadTranslations();
      },
    });
  };

  const downloadBlob = (blob: Blob, fileName: string) => {
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const blob = await statusTranslationService.exportAll();
      const filename = `status_translations_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.xlsx`;
      downloadBlob(blob, filename);
      message.success('导出成功');
    } catch (error) {
      message.error('导出失败，请稍后重试');
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async (file: File) => {
    setImporting(true);
    try {
      const res = await statusTranslationService.importAll(file);
      if (res.success) {
        message.success(res.message || '导入完成');
        await loadTranslations();
        setImportModalVisible(false);
      } else {
        message.error(res.message || '导入失败');
      }
    } catch (error) {
      message.error('导入失败，请检查文件格式');
    } finally {
      setImporting(false);
    }
  };

  const handleResetFilters = () => {
    setSearchText('');
    setNation(undefined);
  };


  const filteredTranslations = useMemo(() => {
    if (!searchText) return translations;
    return translations.filter(item =>
      item.english.toLowerCase().includes(searchText.toLowerCase()) ||
      item.chinese.includes(searchText)
    );
  }, [translations, searchText]);

  // 搜索或国家筛选变化时，重置到第一页
  useEffect(() => {
    setPagination(prev => ({ ...prev, current: 1 }));
  }, [searchText, nation]);

  const columns = [
    {
      title: '序号',
      key: 'index',
      width: 80,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: '国家/地区',
      dataIndex: 'nation',
      key: 'nation',
      width: 220,
      render: (text: string) => text || '-',
    },
    {
      title: '英文状态',
      dataIndex: 'english',
      key: 'english',
      ellipsis: true,
      render: (text: string) => (
        <Tooltip title={text}>
            {text}
        </Tooltip>
      ),
    },
    {
      title: '中文翻译',
      dataIndex: 'chinese',
      key: 'chinese',
      ellipsis: true,
      render: (text: string) => (
        <Tooltip title={text}>
            {text}
        </Tooltip>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      render: (_: any, record: StatusTranslationItem) => (
        <Space size="small">
          <Tooltip title="编辑">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                handleEdit(record);
                setModalVisible(true);
              }}
            />
          </Tooltip>
          <Popconfirm
            title="确定删除这个翻译吗？"
            onConfirm={() => handleDelete(record)}
            okText="确定"
            cancelText="取消"
          >
            <Tooltip title="删除">
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
  };

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={(
          <Space size="small">
            <span>状态翻译</span>
            <ListHelpButton page_key="status_translation" />
          </Space>
        )}
        extra={
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              data-testid="btn-st-add"
            >
              添加翻译
            </Button>

            <Dropdown
              menu={{
                items: [
                  { key: 'export', label: '导出数据', icon: <ExportOutlined />, onClick: handleExport },
                  ...(canImport ? [{ key: 'import', label: '导入数据', icon: <ImportOutlined />, onClick: () => setImportModalVisible(true) }] : []),
                ]
              }}
            >
              <Button data-testid="btn-patent-data-menu">数据管理</Button>
            </Dropdown>

            {selectedRowKeys.length > 0 && (
              <Popconfirm
                title={`确定删除选中的 ${selectedRowKeys.length} 个翻译吗？`}
                okText="确定"
                cancelText="取消"
                onConfirm={async () => {
                  // 收集所有选中记录的 ID
                  const ids: number[] = [];
                  for (const key of selectedRowKeys) {
                    const idx = Number(key);
                    const row = translations[idx] as STItem;
                    if (row?.id) {
                      ids.push(row.id);
                    }
                  }
                  if (ids.length === 0) {
                    message.info('没有可删除的记录');
                    return;
                  }
                  try {
                    const response = await statusTranslationService.batchDelete(ids);
                    if (response.success) {
                      message.success(response.message || `成功删除 ${response.deleted_count || ids.length} 条状态翻译`);
                      setSelectedRowKeys([]);
                      await loadTranslations();
                    } else {
                      message.error(response.message || '批量删除失败');
                    }
                  } catch (e: any) {
                    message.error(e?.response?.data?.message || '批量删除失败');
                  }
                }}
                disabled={selectedRowKeys.length === 0}
              >
                <Button danger disabled={selectedRowKeys.length === 0} icon={<DeleteOutlined />} data-testid="btn-st-batch-delete">
                  批量删除
                </Button>
              </Popconfirm>
            )}
          </Space>
        }
      >

        <div style={{ marginBottom: 16 }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Space>
                <Input
                  placeholder="搜索英文状态或中文翻译"
                  prefix={<SearchOutlined />}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{ width: 250 }}
                  allowClear
                  data-testid="st-search"
                />
                <Select
                  allowClear
                  showSearch
                  placeholder="选择国家/地区"
                  style={{ minWidth: 260 }}
                  value={nation}
                  onChange={(v) => setNation(v)}
                  filterOption={(input, option) => String(option?.children || '').toLowerCase().includes(input.toLowerCase())}
                >
                  {MONITORED_NATION_OPTIONS.map(opt => (
                    <Select.Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Select.Option>
                  ))}
                </Select>
                {searchText && (
                  <Button
                    icon={<ClearOutlined />}
                    onClick={() => setSearchText('')}
                    data-testid="btn-st-clear"
                  >
                    清除搜索
                  </Button>
                )}
              </Space>
            </Col>
            <Col></Col>
          </Row>
        </div>

        {(searchText || nation) && (
          <Row style={{ marginBottom: 16 }}>
            <Col span={24}>
              <div style={{
                padding: '8px 12px',
                backgroundColor: '#f0f2f5',
                borderRadius: '6px',
                fontSize: '14px',
                color: '#666'
              }}>
                <Space wrap>
                  <span>已应用筛选条件：</span>
                  {searchText && (
                    <Tag closable onClose={() => setSearchText('')}>
                      搜索: {searchText}
                    </Tag>
                  )}
                  {nation && (
                    <Tag closable onClose={() => setNation(undefined)}>
                      国家: {MONITORED_NATION_OPTIONS.find(opt => opt.value === nation)?.label || nation}
                    </Tag>
                  )}
                  <Button type="link" size="small" onClick={handleResetFilters}>
                    清除所有筛选
                  </Button>
                </Space>
              </div>
            </Col>
          </Row>
        )}

        <Table
          columns={columns}
          dataSource={filteredTranslations.map((item: any, index) => ({ ...item, key: index }))}
          rowSelection={rowSelection}
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: filteredTranslations.length,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
            onChange: async (page, pageSize) => {
              if (pageSize !== pagination.pageSize) {
                try { await userPreferenceService.savePageSizeSettings('statusTranslations', pageSize); } catch {}
              }
              setPagination({ current: page, pageSize });
            }
          }}
          locale={{ emptyText: '暂无翻译数据，请添加状态翻译' }}
        />

      </Card>

      <Modal
        title="导入状态翻译"
        open={importModalVisible}
        onCancel={() => setImportModalVisible(false)}
        footer={null}
        width={520}
      >
        <Upload.Dragger
          accept=".xlsx,.xls,.csv"
          showUploadList={false}
          beforeUpload={(file) => {
            handleImport(file);
            return false;
          }}
          disabled={importing || exporting || downloadingTemplate}
        >
          <p className="ant-upload-drag-icon">
            <ImportOutlined />
          </p>
          <p className="ant-upload-text">点击或拖拽 Excel 文件到此处上传</p>
          <p className="ant-upload-hint">支持 .xlsx/.xls/.csv，表头：国家/地区、英文状态、中文翻译</p>
        </Upload.Dragger>

        <div style={{ marginTop: 20, textAlign: 'left' }}>
          <ul style={{ paddingLeft: '20px', fontSize: '12px', color: '#666' }}>
            <li>请使用模板整理数据，订单编号或订单ID必须至少填写一项</li>
            <li>年份为必填项，日期字段建议使用 YYYY-MM-DD 格式</li>
            <li>“是否已缴费” 支持 是/否、已缴费/未缴费、1/0 等常见表达</li>
            <li>同一订单 + 年份重复将更新已有记录，否则新增新行</li>
          </ul>
        </div>
      </Modal>

      <Modal
        title={editingIndex !== null ? '编辑状态翻译' : '添加状态翻译'}
        open={modalVisible}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel} data-testid="btn-st-cancel">
            取消
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={() => form.submit()}
            icon={editingIndex !== null ? <EditOutlined /> : <PlusOutlined />}
            data-testid="btn-st-submit"
          >
            {editingIndex !== null ? '更新翻译' : '添加翻译'}
          </Button>,
        ]}
        width={520}
        centered
        destroyOnHidden
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => {
            handleSubmit(values);
            setModalVisible(false);
          }}
        >

          <Form.Item label="国家/地区">
            <Select
              allowClear
              showSearch
              placeholder="选择国家/地区"
              value={formNation}
              onChange={(v) => setFormNation(v)}
              filterOption={(input, option) => String(option?.children || '').toLowerCase().includes(input.toLowerCase())}
              data-testid="st-nation"
            >
              {MONITORED_NATION_OPTIONS.map(opt => (
                <Select.Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="english"
            label="英文状态"
            rules={[
              { required: true, message: '请输入英文状态' },
              { max: STATUS_TRANSLATION_TEXT_MAX_LENGTH, message: `英文状态不能超过${STATUS_TRANSLATION_TEXT_MAX_LENGTH}个字符` }
            ]}
          >
            <Input.TextArea
              placeholder="如: Application Filed"
              autoFocus
              rows={3}
              maxLength={STATUS_TRANSLATION_TEXT_MAX_LENGTH}
              showCount
              data-testid="st-input-english"
            />
          </Form.Item>

          <Form.Item
            name="chinese"
            label="中文翻译"
            rules={[
              { required: true, message: '请输入中文翻译' },
              { max: STATUS_TRANSLATION_TEXT_MAX_LENGTH, message: `中文翻译不能超过${STATUS_TRANSLATION_TEXT_MAX_LENGTH}个字符` }
            ]}
          >
            <Input.TextArea
              placeholder="如: 申请已提交"
              rows={3}
              maxLength={STATUS_TRANSLATION_TEXT_MAX_LENGTH}
              showCount
              data-testid="st-input-chinese"
            />
          </Form.Item>

        </Form>
      </Modal>
    </div>
  );
};

export default StatusTranslation;
